import {
  normalizeRawTransaction,
  type RawTransaction,
} from './raw-transaction';
import { parseTransactionJson } from './transaction-json-import';
import {
  ContractAbiDetailsErrorCode,
  HighLevelSolidityType,
  nativeTokenDecimals,
} from '@mls-wallet/shared-types';
import type {
  ContractFunction,
  PolicyContractInteractionCondition,
  PolicyFunction,
} from '@mls-wallet/shared-types';
import { decodeAbiParameters, formatUnits } from 'viem';
import type { AbiParameter } from 'viem';
import {
  getArrayChildType,
  getParameterStateForAddress,
  getParameterStateForArray,
  getParameterStateForBoolean,
  getParameterStateForBytes,
  getParameterStateForNumber,
  getParameterStateForString,
  getParameterStateForStructOrTuple,
  getValueTypeFromSolidityType,
} from './parameter-inputs/utils';
import type {
  NumberValueType,
  TransactionBuilderParameterState,
} from './parameter-inputs/types';
import type { DecodedContractTransactionPayload } from './types';
import {
  findContractFunctionBySelector,
  toViemAbiParameter,
} from './contract-function';

/** Exact EVM call fields accepted from a custom transaction import. */
export type RawContractTransaction = RawTransaction;

/**
 * Result passed into the builder after ABI discovery: either editable decoded
 * form state or an approved opaque call without function metadata.
 */
export type RawContractTransactionImport =
  | {
      status: 'decoded';
      details: DecodedContractTransactionPayload;
    }
  | {
      status: 'undecoded';
      contractAddress: string;
    };

/**
 * Keep selector lookup failure separate from argument decoding failure. Only a
 * selector absent from an otherwise available ABI may use the opaque path.
 */
type DecodeContractTransactionImportResult =
  | {
      status: 'decoded';
      details: DecodedContractTransactionPayload;
    }
  | {
      status: 'selector-not-found';
    }
  | {
      status: 'arguments-invalid';
    };

const CALLDATA_PATTERN = /^0x[0-9a-fA-F]{8}(?:[0-9a-fA-F]{2})*$/;
/**
 * Allow opaque calldata only when the policy has no function rules and either
 * the contract is unverified or a successfully fetched ABI lacks the selector.
 * Other lookup failures and malformed arguments remain blocking.
 */
export function canImportUndecodedRawTransaction({
  abiErrorCode,
  hasAbiLookupError,
  decodeResult,
  policyAllowsUndecodedCalldata,
}: {
  abiErrorCode: ContractAbiDetailsErrorCode | null;
  hasAbiLookupError: boolean;
  decodeResult: DecodeContractTransactionImportResult | null;
  policyAllowsUndecodedCalldata: boolean;
}): boolean {
  if (!policyAllowsUndecodedCalldata) return false;
  if (abiErrorCode === ContractAbiDetailsErrorCode.UNVERIFIED_CONTRACT) {
    return true;
  }

  return (
    abiErrorCode === null &&
    !hasAbiLookupError &&
    decodeResult?.status === 'selector-not-found'
  );
}

/** Return whether any contract condition restricts one or more functions. */
export function policyHasConfiguredFunctions(
  conditions: PolicyContractInteractionCondition | null | undefined
): boolean {
  return (conditions ?? []).some(
    ({ proxyFunctions, implementationFunctions }) =>
      proxyFunctions.length > 0 || implementationFunctions.length > 0
  );
}

/**
 * Validate and normalize the three raw transaction fields accepted by the
 * custom transaction dialog. Numbers are intentionally rejected for `value`
 * because JSON numbers cannot safely represent arbitrary uint256 values.
 */
export function normalizeRawContractTransaction(
  input: unknown
): RawContractTransaction {
  // Shared payload validation is identical for the single-call and batch JSON importers.
  const transaction = normalizeRawTransaction(input);
  // Case: the existing contract editor requires a function selector, so native sends use batch import.
  if (!CALLDATA_PATTERN.test(transaction.data)) {
    throw new Error(
      'data must be 0x-prefixed calldata containing a 4-byte function selector.'
    );
  }
  return transaction;
}

/**
 * Parse either the minimal raw-call object or one encoded call from a Safe
 * Transaction Builder export, then normalize both formats to the same shape.
 */
export function parseRawContractTransactionJson(
  contents: string,
  selectedNetworkId?: number
): RawContractTransaction {
  const transactions = parseTransactionJson(contents, selectedNetworkId);
  // Case: the existing contract editor can only populate one transaction from a file.
  if (transactions.length !== 1) {
    throw new Error(
      'This importer must contain exactly one transaction. Use batch JSON import for multiple transactions.'
    );
  }
  return normalizeRawContractTransaction(transactions[0]);
}

/**
 * Check the calldata's four-byte selector against policy functions. An empty
 * policy list means that any function is allowed.
 */
export function isCalldataFunctionAllowed(
  transaction: Pick<RawContractTransaction, 'data'>,
  allowedFunctions: PolicyFunction[]
): boolean {
  if (allowedFunctions.length === 0) return true;

  const selector = transaction.data.slice(0, 10).toLowerCase();
  return allowedFunctions.some(
    (func) => func.selector.toLowerCase() === selector
  );
}

/**
 * Decode raw calldata with ABI metadata already returned by the authenticated
 * backend ABI-details endpoint. The result distinguishes a missing selector,
 * which may remain opaque, from invalid arguments for a recognized function.
 */
export function decodeContractTransactionImport({
  transaction,
  contractName,
  implementationFunctions,
  proxyFunctions,
}: {
  transaction: RawContractTransaction;
  contractName: string;
  implementationFunctions: ContractFunction[];
  proxyFunctions: ContractFunction[];
}): DecodeContractTransactionImportResult {
  // A selector absent from the merged proxy and implementation ABI is the only
  // successful lookup state that may continue without decoded arguments.
  const selector = transaction.data.slice(0, 10);
  const selectedFunction = findContractFunctionBySelector(
    [...proxyFunctions, ...implementationFunctions],
    selector
  );
  if (!selectedFunction) {
    return { status: 'selector-not-found' };
  }

  try {
    // `decodeAbiParameters` expects only the encoded arguments, so remove the
    // four-byte selector after translating our function inputs to viem types.
    const inputs = selectedFunction.inputs.map(toViemAbiParameter);
    const encodedParameters =
      `0x${transaction.data.slice(10)}` as `0x${string}`;
    const decodedParameters = decodeAbiParameters(inputs, encodedParameters);

    // Convert decoded values into the same validated state objects used by the
    // editable parameter inputs, including nested arrays and tuples.
    const functionParameters = inputs.map((input, index) =>
      toParameterState(input, decodedParameters[index])
    );
    const value = BigInt(transaction.value);

    return {
      status: 'decoded',
      details: {
        contractAddress: transaction.to,
        contractName,
        implementationFunctions,
        proxyFunctions,
        selectedFunction: {
          selector: selectedFunction.selector,
          name: selectedFunction.name,
          inputs,
          stateMutability: selectedFunction.stateMutability,
        },
        functionParameters,
        nativeTokenValue:
          selectedFunction.stateMutability === 'payable' || value > 0n
            ? {
                value: formatUnits(value, nativeTokenDecimals),
                decimals: nativeTokenDecimals,
              }
            : null,
      },
    };
  } catch {
    // Once the selector is recognized, invalid argument bytes must stay
    // blocking instead of falling through to the opaque import path.
    return { status: 'arguments-invalid' };
  }
}

/**
 * Recursively translate a decoded ABI value into the state shape consumed by
 * the existing transaction-builder inputs.
 */
function toParameterState(
  parameter: AbiParameter,
  decodedValue: unknown
): TransactionBuilderParameterState {
  const valueType = getValueTypeFromSolidityType(parameter.type);

  if (valueType === HighLevelSolidityType.ADDRESS) {
    return getParameterStateForAddress(String(decodedValue));
  }

  if (valueType === HighLevelSolidityType.BOOLEAN) {
    return getParameterStateForBoolean(decodedValue ? 'true' : 'false');
  }

  if (valueType === HighLevelSolidityType.BYTES) {
    return getParameterStateForBytes(String(decodedValue), parameter.type);
  }

  if (valueType === HighLevelSolidityType.STRING) {
    return getParameterStateForString(String(decodedValue));
  }

  if (valueType === HighLevelSolidityType.NUMBER) {
    const value: NumberValueType = {
      value: String(decodedValue),
      decimals: 0,
      formatType: 'number',
    };
    return getParameterStateForNumber(value, parameter.type.startsWith('int'));
  }

  if (valueType === HighLevelSolidityType.ARRAY) {
    const childType = getArrayChildType(parameter.type);
    const childParameter: AbiParameter = {
      ...parameter,
      type: childType ?? parameter.type,
    };
    const childStates = Array.isArray(decodedValue)
      ? decodedValue.map((childValue) =>
          toParameterState(childParameter, childValue)
        )
      : [];
    return getParameterStateForArray(childStates, parameter.type);
  }

  if (valueType === HighLevelSolidityType.STRUCT_OR_TUPLE) {
    const components =
      (parameter as AbiParameter & { components?: AbiParameter[] })
        .components ?? [];
    return getParameterStateForStructOrTuple(
      components.map((component, index) =>
        toParameterState(
          component,
          getDecodedTupleComponent(decodedValue, component, index)
        )
      )
    );
  }

  return {
    value: String(decodedValue ?? ''),
    isEmpty: decodedValue === undefined || decodedValue === null,
    isValid: true,
    parsedValue: String(decodedValue ?? ''),
  };
}

/**
 * Read a tuple component from either viem's positional array representation or
 * its named-object representation.
 */
function getDecodedTupleComponent(
  decodedValue: unknown,
  component: AbiParameter,
  index: number
): unknown {
  if (Array.isArray(decodedValue)) {
    return decodedValue[index];
  }

  if (decodedValue && typeof decodedValue === 'object') {
    const tupleRecord = decodedValue as Record<string, unknown>;
    if (component.name && component.name in tupleRecord) {
      return tupleRecord[component.name];
    }
    return Object.values(tupleRecord)[index];
  }

  return undefined;
}
